type SpeakingTile = { participant: { identity: string } };

/**
 * Keep the viewer's tile order. A person already on page 1 only gets the
 * speaking highlight. Someone speaking on a later page swaps into the first
 * quiet slot on the earliest earlier page. When every slot on that page is
 * speaking, the next page is used. Stopping speech rebuilds from the original
 * order, so the card returns to its slot.
 */
export function promoteOffPageSpeakers<T extends SpeakingTile>(
  tracks: readonly T[],
  speakingIds: ReadonlySet<string>,
  pageSize: number,
): readonly T[] {
  if (!speakingIds.size || pageSize < 1 || tracks.length <= pageSize) {
    return tracks;
  }

  const next = tracks.slice();
  const speaking = (tile: T) => speakingIds.has(tile.participant.identity);
  let changed = false;

  for (let i = pageSize; i < next.length; i++) {
    if (!speaking(next[i])) continue;
    const speakerPage = Math.floor(i / pageSize);
    let slot = -1;
    for (let page = 0; page < speakerPage; page++) {
      const pageStart = page * pageSize;
      const pageEnd = Math.min(next.length, pageStart + pageSize);
      for (let j = pageStart; j < pageEnd; j++) {
        if (!speaking(next[j])) {
          slot = j;
          break;
        }
      }
      if (slot >= 0) break;
    }
    if (slot < 0) continue;
    const displaced = next[slot];
    next[slot] = next[i];
    next[i] = displaced;
    changed = true;
  }

  return changed ? next : tracks;
}
